import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaCalculator, FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import ConversionExplanationDecimalToAll from './ConversionExplanationDecimalToAll';
import ConversionFlow from '../../ConversionFlow';

import { specialConversions } from '../../../../utils/unitConversions/number/numberUtils';

const ConversionExplanationBase64ToAll = ({ conversionType, fromUnit, toUnit, inputValue, result, darkMode }) => {
  const [showIntermediateConversion, setShowIntermediateConversion] = useState(false);
  const [showDetailedStep2, setShowDetailedStep2] = useState(true);
  const [showQuiz, setShowQuiz] = useState(false);
  const [quizAnswer, setQuizAnswer] = useState('');
  const [quizResult, setQuizResult] = useState(null);
  const [currentStep, setCurrentStep] = useState(0);
  const [isConversionRendered, setIsConversionRendered] = useState(false);

  const handleQuizSubmit = useCallback(() => {
    const isCorrect = quizAnswer.toLowerCase() === result.toLowerCase();
    setQuizResult(isCorrect);
  }, [quizAnswer, result]);

  const renderExampleOrConversionText = useCallback((base64Value, isExample) => (
    isExample ? (
      <p className="mb-4 text-yellow-600 italic">
        Example: Let's convert the Base-64 number {base64Value} to {toUnit}.
      </p>
    ) : (
      <p className="mb-4 text-green-600">
        Your conversion: {base64Value} (Base-64) to {toUnit}
      </p>
    )
  ), [toUnit]);

  const toggleIntermediateConversion = () => setShowIntermediateConversion(!showIntermediateConversion);
  const toggleDetailedStep2 = () => setShowDetailedStep2(!showDetailedStep2);

  const getIntermediateConversionExplanation = useCallback((base64Value) => {
    const decimalValue = specialConversions['Base-64'].toDecimal(base64Value);
    const steps = base64Value.split('');

    const renderBase64ToDecimalConversion = () => (
      <div className="relative mt-12 flex flex-col items-center">
        <div className="absolute top-0 left-0 text-2xl font-bold main-number bg-gray-100 dark:bg-gray-700 p-2 rounded-md shadow-md">
          {base64Value.split('').map((char, index) => (
            <span key={index} className={`transition-colors duration-300 ${steps.length - 1 - index === currentStep ? 'text-blue-500 dark:text-blue-400' : 'dark:text-white'}`}>
              {char}
            </span>
          ))}
          <sub className="dark:text-white">64</sub>
        </div>
        <div className="flex flex-col items-center space-y-4 mt-16">
          {steps.map((char, index) => {
            const value = specialConversions['Base-64'].charToValue(char) * Math.pow(64, steps.length - 1 - index);
            const isActive = index === currentStep;
            const isCompleted = index < currentStep;
            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.1 }}
                className={`flex items-center space-x-4 p-4 rounded-lg shadow-md ${isActive ? 'bg-blue-50 dark:bg-blue-900' :
                    isCompleted ? 'text-gray-500 dark:text-gray-300 bg-gray-50 dark:bg-gray-800' : 'bg-white dark:bg-gray-700'
                  } transition-all duration-300`}
              >
                <div className={`w-12 text-right digit ${isActive ? 'text-blue-500 dark:text-blue-400 font-bold' : 'dark:text-white'} transition-colors duration-300`}>{char}</div>
                <div className="w-8 text-center dark:text-white">×</div>
                <div className="w-16 text-center group relative dark:text-white">
                  64<sup>{steps.length - 1 - index}</sup>
                  <span className="absolute bottom-full left-1/2 transform -translate-x-1/2 bg-gray-800 text-white dark:bg-gray-200 dark:text-gray-800 text-xs rounded py-1 px-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    64{steps.length - 1 - index > 0 ? <sup>{steps.length - 1 - index}</sup> : ''} = {Math.pow(64, steps.length - 1 - index)}
                  </span>
                </div>
                <div className="w-8 text-center dark:text-white">=</div>
                <div className={`w-20 text-left calculation ${isActive ? 'text-blue-500 dark:text-blue-400 font-bold' : 'dark:text-white'} transition-colors duration-300`}>{value}</div>
              </motion.div>
            );
          })}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: currentStep === steps.length ? 1 : 0, y: currentStep === steps.length ? 0 : 10 }}
            className="font-bold text-xl mt-6 pt-4 border-t border-gray-300 dark:border-gray-600 dark:text-white"
          >
            Sum: {decimalValue} (Decimal)
          </motion.div>
        </div>
      </div>
    );

    return (
      <AnimatePresence>
        {showIntermediateConversion && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className={`mt-6 p-6 ${darkMode ? 'bg-gray-800' : 'bg-gray-50'} rounded-lg shadow-lg relative overflow-hidden`}
            style={{
              background: darkMode
                ? 'linear-gradient(145deg, #2d3748, #1a202c)'
                : 'linear-gradient(145deg, #f7fafc, #edf2f7)'
            }}
          >
            <h4 className="text-2xl font-semibold mb-4 flex items-center">
              <FaCalculator className="mr-2" />
              Base-64 to Decimal Conversion
            </h4>
            <p className="mb-6 leading-relaxed">
              To convert Base-64 to decimal, we multiply each character's value by 64 raised to the power of its position (starting from the right) and sum the results.
            </p>

            {renderBase64ToDecimalConversion()}

            <div className="mt-8 flex justify-between">
              <button
                onClick={() => setCurrentStep(Math.max(0, currentStep - 1))}
                className={`px-4 py-2 flex items-center ${darkMode ? 'bg-blue-600 hover:bg-blue-700' : 'bg-blue-500 hover:bg-blue-600'
                  } text-white rounded-md transition-all duration-300 transform hover:scale-105`}
                disabled={currentStep === 0}
              >
                <FaChevronLeft className="mr-2" /> Previous
              </button>
              <button
                onClick={() => setCurrentStep(Math.min(steps.length, currentStep + 1))}
                className={`px-4 py-2 flex items-center ${darkMode ? 'bg-blue-600 hover:bg-blue-700' : 'bg-blue-500 hover:bg-blue-600'
                  } text-white rounded-md transition-all duration-300 transform hover:scale-105`}
                disabled={currentStep === steps.length}
              >
                Next <FaChevronRight className="ml-2" />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    );
  }, [showIntermediateConversion, darkMode, currentStep]);

  const getBase64ToDecimalExplanation = useCallback((base64Value, isExample, exampleResult) => {
    const decimalValue = specialConversions['Base-64'].toDecimal(base64Value);

    const steps = base64Value.split('').reverse().map((char, index) => ({
      char,
      value: specialConversions['Base-64'].charToValue(char),
      power: index,
      result: specialConversions['Base-64'].charToValue(char) * Math.pow(64, index)
    }));

    return (
      <div className="mt-6">
        <h3 className="text-xl font-semibold mb-4">How Base-64 to Decimal Conversion Works</h3>
        {renderExampleOrConversionText(base64Value, isExample)}
        <p className="mb-4">
          To convert Base-64 to decimal, we multiply each character's value by 64 raised to the power of its position (starting from the right) and sum the results.
        </p>
        <div className="overflow-x-auto">
          <table className={`w-full text-left ${darkMode ? 'text-white' : 'text-gray-900'}`}>
            <thead>
              <tr>
                <th className="px-4 py-2">Character</th>
                <th className="px-4 py-2">Value</th>
                <th className="px-4 py-2">Position</th>
                <th className="px-4 py-2">Calculation</th>
                <th className="px-4 py-2">Result</th>
              </tr>
            </thead>
            <tbody>
              {steps.map((step, index) => (
                <tr
                  key={index}
                  className={index % 2 === 0 ? (darkMode ? 'bg-gray-700' : 'bg-gray-100') : ''}
                >
                  <td className="border px-4 py-2">{step.char}</td>
                  <td className="border px-4 py-2">{step.value}</td>
                  <td className="border px-4 py-2">{step.power}</td>
                  <td className="border px-4 py-2">{step.value} × 64<sup>{step.power}</sup></td>
                  <td className="border px-4 py-2">{step.result}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="mt-4">
          Sum of all results: <span className="font-bold text-purple-500">{decimalValue}</span> (Decimal)
        </p>
        <p className="mt-4 font-bold">
          Therefore, <span className="text-blue-500">{base64Value}</span> (Base-64) = <span className="text-purple-500">{isExample ? exampleResult : decimalValue}</span> (Decimal)
        </p>
      </div>
    );
  }, [darkMode, renderExampleOrConversionText]);

  const getBase64ToOtherBaseExplanation = useCallback((base64Value, isExample, base, baseLabel, exampleResult) => {
    const decimalValue = specialConversions['Base-64'].toDecimal(base64Value);
    let convertedValue;
    
    if (isExample) {
      // For examples, we need to convert the decimal value to the target base
      if (baseLabel === 'Hexadecimal') {
        convertedValue = decimalValue.toString(16).toUpperCase();
      } else if (baseLabel === 'Binary') {
        convertedValue = decimalValue.toString(2);
      } else if (baseLabel === 'Octal') {
        convertedValue = decimalValue.toString(8);
      } else if (baseLabel === 'Roman Numeral') {
        convertedValue = specialConversions['Roman Numeral'].fromDecimal(decimalValue);
      } else if (baseLabel === 'Scientific Notation') {
        convertedValue = specialConversions['Scientific Notation'].fromDecimal(decimalValue);
      } else {
        convertedValue = exampleResult;
      }
    } else {
      convertedValue = result;
    }

    const CompactExplanation = () => (
      <div className="mt-4 p-6 bg-gradient-to-r from-blue-50 to-green-50 dark:from-blue-900 dark:to-green-900 rounded-lg shadow-md">
        <p className="text-center font-bold text-2xl mb-4">
          <span className="text-blue-600 dark:text-blue-400">{base64Value}</span><sub className="text-black dark:text-white">64</sub> →
          <span className="text-purple-600 dark:text-purple-400">{decimalValue}</span><sub className="text-black dark:text-white">10</sub> →
          <span className="text-green-600 dark:text-green-400">{convertedValue}</span>
          {baseLabel !== 'Roman Numeral' && baseLabel !== 'Scientific Notation' && baseLabel !== 'Decimal' && (
            <sub className="text-black dark:text-white font-bold">{base}</sub>
          )}
        </p>
        <div className="grid grid-cols-3 gap-4 text-center">
          <div>
            <p className="font-semibold text-blue-600 dark:text-blue-400">Base-64</p>
            <p className="text-lg">{base64Value}</p>
          </div>
          <div>
            <p className="font-semibold text-purple-600 dark:text-purple-400">Decimal</p>
            <p className="text-lg">{decimalValue}</p>
          </div>
          <div>
            <p className="font-semibold text-green-600 dark:text-green-400">{baseLabel}</p>
            <p className="text-lg">{convertedValue}</p>
          </div>
        </div>
      </div>
    );

    return (
      <div className="mt-6">
        <h3 className="text-xl font-semibold mb-4">How Base-64 to {baseLabel} Conversion Works</h3>
        {renderExampleOrConversionText(base64Value, isExample)}
        <p className="mb-4">
          To convert a Base-64 number to {baseLabel}, we first convert the Base-64 number to decimal, and then we convert the decimal to {baseLabel}.
        </p>
        <ConversionFlow
          steps={['Base-64', 'Decimal', baseLabel]}
          darkMode={darkMode}
        />
        <ol className="list-decimal list-inside mb-4">
          <li className="mb-2">
            Convert Base-64 to decimal: {base64Value}<sub>64</sub> = {decimalValue}<sub>10</sub>
            <button
              onClick={toggleIntermediateConversion}
              className={`ml-2 px-2 py-1 text-sm ${darkMode ? 'bg-blue-600 hover:bg-blue-700' : 'bg-blue-500 hover:bg-blue-600'} text-white rounded-md transition-colors`}
            >
              {showIntermediateConversion ? 'Hide Details' : 'Show Details'}
            </button>
          </li>
          {getIntermediateConversionExplanation(base64Value)}
          <li className="mb-2">
            Now, we convert the decimal number <span className="font-bold text-blue-500">{decimalValue}</span> to {baseLabel}:
            <button
              onClick={toggleDetailedStep2}
              className={`ml-2 px-2 py-1 text-sm ${darkMode ? 'bg-blue-600 hover:bg-blue-700' : 'bg-blue-500 hover:bg-blue-600'} text-white rounded-md transition-colors`}
            >
              {showDetailedStep2 ? 'Hide Details' : 'Show Details'}
            </button>
            {showDetailedStep2 && (
              <p className="mt-2 text-center font-semibold">
                <span className="text-blue-500">{base64Value}</span><sub className="text-black">64</sub> →
                <span className="text-blue-500">{decimalValue}</span><sub className="text-black">10</sub> →
                <span className="text-blue-500">...</span>
                {baseLabel !== 'Roman Numeral' && baseLabel !== 'Scientific Notation' && baseLabel !== 'Decimal' && (
                  <sub className="text-black font-bold">{base}</sub>
                )}
              </p>
            )}
            {showDetailedStep2 ? (
              <>
                <AnimatePresence>
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.3 }}
                    className="mt-2 p-4 bg-gray-100 dark:bg-gray-800 rounded-md overflow-hidden"
                  >
                    <ConversionExplanationDecimalToAll
                      conversionType="number"
                      fromUnit="Decimal"
                      toUnit={baseLabel}
                      inputValue={decimalValue.toString()}
                      result={convertedValue}
                      darkMode={darkMode}
                    />
                  </motion.div>
                </AnimatePresence>
              </>
            ) : (
              <CompactExplanation />
            )}
          </li>
        </ol>
        {showDetailedStep2 && (
          <>
            <p className="mt-4 text-center font-semibold text-lg">
              Complete conversion flow:
            </p>
            <p className="mt-2 text-center">
              <span className="font-bold text-blue-500">{base64Value}<sub className="text-black">64</sub></span> →
              <span className="font-bold text-blue-500">{decimalValue}<sub className="text-black">10</sub></span> →
              <span className="font-bold text-green-500">{convertedValue}</span>
              {baseLabel !== 'Roman Numeral' && baseLabel !== 'Scientific Notation' && <sub className="text-black">{base}</sub>}
            </p>
          </>
        )}
        <p className="mt-4 font-bold">
          Therefore, <span className="text-blue-500">{base64Value}</span> (Base-64) = <span className="text-green-500">{convertedValue}</span> ({baseLabel})
        </p>
      </div>
    );
  }, [darkMode, renderExampleOrConversionText, getIntermediateConversionExplanation, showIntermediateConversion, showDetailedStep2, result, toggleIntermediateConversion, toggleDetailedStep2]);

  const getExplanation = useMemo(() => {
    const base64Value = inputValue || 'QQ==';
    const isExample = !result;
    const exampleResult = isExample ? specialConversions['Base-64'].toDecimal(base64Value).toString() : result;

    if (toUnit === 'Decimal') {
      return getBase64ToDecimalExplanation(base64Value, isExample, exampleResult);
    } else {
      return getBase64ToOtherBaseExplanation(base64Value, isExample, toUnit === 'Binary' ? 2 : toUnit === 'Octal' ? 8 : toUnit === 'Hexadecimal' ? 16 : 36, toUnit, exampleResult);
    }
  }, [inputValue, toUnit, result, getBase64ToDecimalExplanation, getBase64ToOtherBaseExplanation]);

  useEffect(() => {
    if (showIntermediateConversion) {
      const timer = setTimeout(() => setIsConversionRendered(true), 100);
      return () => clearTimeout(timer);
    } else {
      setIsConversionRendered(false);
    }
  }, [showIntermediateConversion]);

  return (
    <div className={`p-6 ${darkMode ? 'bg-gray-900 text-white' : 'bg-white text-gray-800'} font-sans leading-relaxed`}>
      {getExplanation}
      <div className="mt-6">
        <button
          onClick={() => setShowQuiz(!showQuiz)}
          className={`px-4 py-2 ${darkMode ? 'bg-blue-600 hover:bg-blue-700' : 'bg-blue-500 hover:bg-blue-600'} text-white rounded-md transition-colors`}
        >
          {showQuiz ? 'Hide Quiz' : 'Test Your Knowledge'}
        </button>
      </div>

      {showQuiz && (
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className={`mt-4 p-4 ${darkMode ? 'bg-gray-700' : 'bg-gray-100'} rounded-md`}
        >
          <h4 className="font-semibold">Quick Quiz:</h4>
          <p>Convert the Base-64 number {inputValue || 'QQ=='} to {toUnit.toLowerCase()}:</p>
          <input
            type="text"
            value={quizAnswer}
            onChange={(e) => setQuizAnswer(e.target.value)}
            className={`mt-2 px-2 py-1 border ${darkMode ? 'bg-gray-600 border-gray-500 text-white' : 'bg-white border-gray-300'} rounded-md`}
          />
          <button
            onClick={handleQuizSubmit}
            className={`ml-2 px-4 py-1 ${darkMode ? 'bg-green-600 hover:bg-green-700' : 'bg-green-500 hover:bg-green-600'} text-white rounded-md transition-colors`}
          >
            Submit
          </button>
          {quizResult !== null && (
            <p className={`mt-2 text-lg font-bold ${quizResult ? 'text-green-500' : 'text-red-500'}`}>
              {quizResult ? 'Correct!' : `Try again! The correct answer is ${result}.`}
            </p>
          )}
        </motion.div>
      )}
    </div>
  );
};

export default React.memo(ConversionExplanationBase64ToAll);
              